<!--
  DAVMAN AI INTEGRATION
  Este archivo es parte de la integración personalizada de IA para Davman Persa.
  NO es parte del código original de Chatwoot.

  Pestaña de configuración general del asistente IA
-->
<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { getSettings, updateSettings } from 'dashboard/api/davmanAi';

import Button from 'dashboard/components-next/button/Button.vue';

const loading = ref(true);
const saving = ref(false);
const settings = ref({
  suggestions_enabled: false,
  operation_mode: 'suggestions_only',
  auto_reply_faqs: false,
  auto_reply_welcome: true,
  alert_on_complaints: true,
  primary_llm: 'claude',
  fallback_llm: 'openai',
  max_context_messages: 10,
  response_timeout_ms: 15000,
  welcome_message: '',
  out_of_hours_message: '',
});

const operationModes = [
  {
    value: 'suggestions_only',
    label: 'Solo sugerencias',
    description: 'La IA genera sugerencias que el agente puede usar o ignorar',
  },
  {
    value: 'auto_faqs',
    label: 'FAQs automáticas',
    description:
      'Responde automáticamente a preguntas frecuentes, sugerencias para el resto',
  },
  {
    value: 'full_auto',
    label: 'Completamente automático',
    description: 'La IA responde automáticamente a todos los mensajes',
  },
];

const llmOptions = [
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'openai', label: 'GPT-4 (OpenAI)' },
];

const loadSettings = async () => {
  loading.value = true;
  try {
    const data = await getSettings();
    settings.value = {
      suggestions_enabled: data.suggestions_enabled === true,
      operation_mode: data.operation_mode || 'suggestions_only',
      auto_reply_faqs: data.auto_reply_faqs || false,
      auto_reply_welcome: data.auto_reply_welcome !== false,
      alert_on_complaints: data.alert_on_complaints !== false,
      primary_llm: data.primary_llm || 'claude',
      fallback_llm: data.fallback_llm || 'openai',
      max_context_messages: data.max_context_messages || 10,
      response_timeout_ms: data.response_timeout_ms || 15000,
      welcome_message: data.welcome_message || '',
      out_of_hours_message: data.out_of_hours_message || '',
    };
  } catch (error) {
    useAlert('Error al cargar la configuración');
    console.error('[Davman AI] Error loading settings:', error);
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  saving.value = true;
  try {
    await updateSettings(settings.value);
    useAlert('Configuración guardada correctamente');
  } catch (error) {
    useAlert('Error al guardar la configuración');
    console.error('[Davman AI] Error saving settings:', error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadSettings();
});
</script>

<template>
  <div v-if="loading" class="flex items-center justify-center py-12">
    <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
  </div>

  <div v-else class="flex flex-col gap-8">
    <!-- Toggle de sugerencias -->
    <div
      class="flex items-center justify-between p-5 rounded-xl border transition-all"
      :class="
        settings.suggestions_enabled
          ? 'border-green-500/40 bg-green-500/5'
          : 'border-n-weak bg-n-solid-2'
      "
    >
      <div class="flex items-center gap-4">
        <span
          class="flex items-center justify-center size-10 rounded-full"
          :class="
            settings.suggestions_enabled
              ? 'bg-green-500/10 text-green-600'
              : 'bg-n-solid-3 text-n-slate-11'
          "
        >
          <span
            :class="
              settings.suggestions_enabled
                ? 'i-lucide-sparkles'
                : 'i-lucide-pause'
            "
            class="size-5"
          />
        </span>
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            Sugerencias de IA
          </h4>
          <p class="text-xs text-n-slate-11">
            {{
              settings.suggestions_enabled
                ? 'El asistente genera sugerencias en tiempo real para cada mensaje entrante.'
                : 'Las sugerencias estan pausadas. No se consumen tokens ni se procesan mensajes.'
            }}
          </p>
        </div>
      </div>
      <label class="relative inline-flex items-center cursor-pointer">
        <input
          v-model="settings.suggestions_enabled"
          type="checkbox"
          class="sr-only peer"
        />
        <div
          class="w-11 h-6 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2"
          :class="
            settings.suggestions_enabled
              ? 'bg-green-500 peer-focus:ring-green-500/30'
              : 'bg-n-solid-3 peer-focus:ring-n-solid-3/30'
          "
        >
          <div
            class="absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform"
            :class="settings.suggestions_enabled ? 'translate-x-5' : ''"
          />
        </div>
      </label>
    </div>

    <!-- Modo de operación -->
    <div class="flex flex-col gap-4">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12 mb-1">
          Modo de Operación
        </h4>
        <p class="text-xs text-n-slate-11">
          Define cómo el asistente interactúa con las conversaciones.
        </p>
      </div>

      <div class="grid gap-3 md:grid-cols-3">
        <label
          v-for="mode in operationModes"
          :key="mode.value"
          class="relative flex flex-col p-4 rounded-xl border cursor-pointer transition-all"
          :class="
            settings.operation_mode === mode.value
              ? 'border-[#87785B] bg-[#87785B]/5'
              : 'border-n-weak hover:border-n-strong'
          "
        >
          <input
            v-model="settings.operation_mode"
            type="radio"
            :value="mode.value"
            class="absolute opacity-0"
          />
          <span class="font-medium text-n-slate-12">{{ mode.label }}</span>
          <span class="text-xs text-n-slate-11 mt-1">
            {{ mode.description }}
          </span>
          <span
            v-if="settings.operation_mode === mode.value"
            class="absolute top-2 right-2 i-lucide-check size-5"
            style="color: #87785B"
          />
        </label>
      </div>
    </div>

    <!-- Opciones de auto-respuesta -->
    <div class="flex flex-col gap-4">
      <h4 class="text-sm font-medium text-n-slate-12">
        Opciones de Auto-respuesta
      </h4>

      <div class="flex flex-col gap-3">
        <label class="flex items-center gap-3 cursor-pointer">
          <input
            v-model="settings.auto_reply_faqs"
            type="checkbox"
            class="size-4 rounded border-n-weak"
          />
          <div>
            <span class="text-sm text-n-slate-12">
              Responder FAQs automáticamente
            </span>
            <p class="text-xs text-n-slate-11">
              Cuando detecte una FAQ con alta confianza, responder sin esperar
              al agente.
            </p>
          </div>
        </label>

        <label class="flex items-center gap-3 cursor-pointer">
          <input
            v-model="settings.auto_reply_welcome"
            type="checkbox"
            class="size-4 rounded border-n-weak"
          />
          <div>
            <span class="text-sm text-n-slate-12">
              Mensaje de bienvenida automático
            </span>
            <p class="text-xs text-n-slate-11">
              Enviar un saludo inicial cuando un cliente inicia una
              conversación.
            </p>
          </div>
        </label>

        <label class="flex items-center gap-3 cursor-pointer">
          <input
            v-model="settings.alert_on_complaints"
            type="checkbox"
            class="size-4 rounded border-n-weak"
          />
          <div>
            <span class="text-sm text-n-slate-12">
              Alertar sobre quejas
            </span>
            <p class="text-xs text-n-slate-11">
              Notificar al agente cuando se detecte un cliente insatisfecho o
              queja.
            </p>
          </div>
        </label>
      </div>
    </div>

    <!-- Mensajes predefinidos -->
    <div class="flex flex-col gap-4">
      <h4 class="text-sm font-medium text-n-slate-12">
        Mensajes Predefinidos
      </h4>

      <div>
        <label class="block text-sm text-n-slate-11 mb-1">
          Mensaje de bienvenida
        </label>
        <textarea
          v-model="settings.welcome_message"
          placeholder="¡Bienvenido a Davman Persa! Será un placer atenderle..."
          rows="3"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 resize-y focus:border-n-strong focus:outline-none"
        />
      </div>

      <div>
        <label class="block text-sm text-n-slate-11 mb-1">
          Mensaje fuera de horario
        </label>
        <textarea
          v-model="settings.out_of_hours_message"
          placeholder="Gracias por contactarnos. Nuestro horario de atención es..."
          rows="3"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 resize-y focus:border-n-strong focus:outline-none"
        />
      </div>
    </div>

    <!-- Configuración técnica -->
    <div class="flex flex-col gap-4">
      <h4 class="text-sm font-medium text-n-slate-12">
        Configuración Técnica
      </h4>

      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            Modelo LLM primario
          </label>
          <select
            v-model="settings.primary_llm"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            <option
              v-for="opt in llmOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            Modelo LLM de respaldo
          </label>
          <select
            v-model="settings.fallback_llm"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            <option
              v-for="opt in llmOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            Mensajes de contexto máximos
          </label>
          <input
            v-model.number="settings.max_context_messages"
            type="number"
            min="1"
            max="50"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            Cuántos mensajes anteriores incluir como contexto.
          </p>
        </div>

        <div>
          <label class="block text-sm text-n-slate-11 mb-1">
            Timeout de respuesta (ms)
          </label>
          <input
            v-model.number="settings.response_timeout_ms"
            type="number"
            min="5000"
            max="60000"
            step="1000"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            Tiempo máximo de espera para generar una respuesta.
          </p>
        </div>
      </div>
    </div>

    <!-- Botón guardar -->
    <div class="flex justify-end pt-4 border-t border-n-weak">
      <Button
        label="Guardar Cambios"
        icon="i-lucide-save"
        :is-loading="saving"
        @click="saveSettings"
      />
    </div>
  </div>
</template>
